import { startTransition, useEffect, useMemo, useState } from "react";
import type {
  CrewSearchResult,
  ProjectRecord,
  RoleRecord,
  TeamCalendarResponse,
  UnavailabilityRecord,
  UserListRecord,
  UserSessionView,
} from "../shared/models";
import { CalendarView } from "./components/CalendarView";
import { AddCrewAccountsPage } from "./components/AddCrewAccountsPage";
import { LoginCodePage } from "./components/LoginCodePage";
import { OnboardCrewMemberPage } from "./components/OnboardCrewMemberPage";
import { CrewProfileDrawer } from "./components/CrewProfileDrawer";
import { RoleConfirmationPage } from "./components/RoleConfirmationPage";
import { SelectedCrewTray, type SelectedCrewMember } from "./components/SelectedCrewTray";
import { ApiError, addDaysToDateInput, apiRequest, formatDate, formatDateRange, todayDateInputValue } from "./lib/api";

type Page = "my-unavailability" | "find-crew" | "team-calendar" | "crew" | "onboard-crew-member" | "add-crew-accounts" | "roles" | "projects";

function buildDefaultRange() {
  return {
    start: todayDateInputValue(),
    end: addDaysToDateInput(todayDateInputValue(), 6),
  };
}

function navForRole(role: UserSessionView["app_role"]): Array<{ key: Page; label: string }> {
  if (role === "crew") {
    return [{ key: "my-unavailability", label: "My Unavailability" }];
  }

  if (role === "pm") {
    return [
      { key: "find-crew", label: "Find Crew" },
      { key: "team-calendar", label: "Team Calendar" },
      { key: "projects", label: "Projects" },
    ];
  }

  return [
    { key: "crew", label: "Crew" },
    { key: "onboard-crew-member", label: "Onboard Crew Member" },
    { key: "add-crew-accounts", label: "Add Crew Accounts" },
    { key: "roles", label: "Roles" },
    { key: "projects", label: "Projects" },
    { key: "find-crew", label: "Find Crew" },
    { key: "team-calendar", label: "Team Calendar" },
    { key: "my-unavailability", label: "My Unavailability" },
  ];
}

function csvValue(value: string) {
  return `"${value.replaceAll('"', '""')}"`;
}

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

export default function App() {
  const [authPath, setAuthPath] = useState<"password" | "login-code">(
    typeof window !== "undefined" && window.location.pathname === "/login-code" ? "login-code" : "password",
  );
  const [session, setSession] = useState<UserSessionView | null>(null);
  const [page, setPage] = useState<Page>("my-unavailability");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [projects, setProjects] = useState<ProjectRecord[]>([]);
  const [users, setUsers] = useState<Array<UserListRecord & { role_ids?: number[]; role_names?: string[] }>>([]);
  const [myUnavailability, setMyUnavailability] = useState<UnavailabilityRecord[]>([]);
  const [searchResults, setSearchResults] = useState<CrewSearchResult[]>([]);
  const [teamCalendar, setTeamCalendar] = useState<TeamCalendarResponse | null>(null);
  const [selectedCrewProfile, setSelectedCrewProfile] = useState<(UserListRecord & { role_names?: string[]; email: string }) | null>(null);
  const [selectedCrewCalendar, setSelectedCrewCalendar] = useState<TeamCalendarResponse | null>(null);
  const [selectedCrew, setSelectedCrew] = useState<Record<number, SelectedCrewMember>>({});
  const [selectionProjectId, setSelectionProjectId] = useState("");

  const defaultRange = useMemo(buildDefaultRange, []);
  const [searchForm, setSearchForm] = useState({
    role_id: "",
    start_date: defaultRange.start,
    end_date: defaultRange.end,
    project_id: "",
    notes: "",
  });
  const [unavailabilityForm, setUnavailabilityForm] = useState({
    start_date: defaultRange.start,
    end_date: defaultRange.end,
    reason: "",
  });
  const [projectForm, setProjectForm] = useState({ name: "", notes: "" });
  const [loginForm, setLoginForm] = useState({ email: "admin@crewnovator.local", password: "CrewOps123!" });
  const [userForm, setUserForm] = useState({
    id: 0,
    email: "",
    password: "CrewOps123!",
    name: "",
    app_role: "crew" as UserSessionView["app_role"],
    phone: "",
    department: "",
    notes: "",
    active: true,
    role_ids: [] as number[],
  });

  function resetUserForm() {
    setUserForm({
      id: 0,
      email: "",
      password: "CrewOps123!",
      name: "",
      app_role: "crew",
      phone: "",
      department: "",
      notes: "",
      active: true,
      role_ids: [],
    });
  }
  const selectedCrewItems = useMemo(
    () => Object.values(selectedCrew).sort((left, right) => left.name.localeCompare(right.name)),
    [selectedCrew],
  );
  const selectedCrewIds = useMemo(() => selectedCrewItems.map((item) => item.user_id), [selectedCrewItems]);

  function getProjectName(project_id?: string | number | null) {
    if (!project_id) {
      return null;
    }
    const id = Number(project_id);
    return projects.find((project) => project.id === id)?.name ?? null;
  }

  function toggleSelectedCrewEntry(entry: SelectedCrewMember) {
    setSelectedCrew((current) => {
      if (current[entry.user_id]) {
        const next = { ...current };
        delete next[entry.user_id];
        return next;
      }
      return {
        ...current,
        [entry.user_id]: entry,
      };
    });
  }

  async function toggleSelectedCrewFromCalendar(user: UserListRecord) {
    if (selectedCrew[user.id]) {
      setSelectedCrew((current) => {
        const next = { ...current };
        delete next[user.id];
        return next;
      });
      return;
    }

    try {
      const cachedUser = users.find((entry) => entry.id === user.id && entry.role_names?.length);
      const role_names = cachedUser?.role_names?.length
        ? cachedUser.role_names
        : (await apiRequest<{ user: UserListRecord & { role_names: string[]; email: string } }>(`/api/users/${user.id}`)).user.role_names;

      toggleSelectedCrewEntry({
        user_id: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        roles: role_names,
        project_name: getProjectName(selectionProjectId),
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not select crew member");
    }
  }

  function toggleSelectedCrewFromSearchResult(result: CrewSearchResult) {
    if (!selectionProjectId && searchForm.project_id) {
      setSelectionProjectId(searchForm.project_id);
    }
    toggleSelectedCrewEntry({
      user_id: result.user_id,
      name: result.name,
      email: result.email,
      phone: result.phone,
      roles: result.all_role_names,
      project_name: getProjectName(searchForm.project_id),
    });
  }

  function clearSelectedCrew() {
    setSelectedCrew({});
  }

  function exportSelectedCrewCsv() {
    const projectName = getProjectName(selectionProjectId);
    const lines = [
      ["Name", "Roles", "Email", "Phone", "Project"].map(csvValue).join(","),
      ...selectedCrewItems.map((item) => [
        item.name,
        item.roles.join("; "),
        item.email,
        item.phone ?? "",
        projectName ?? item.project_name ?? "",
      ].map(csvValue).join(",")),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "crewnovator-selected-crew.csv";
    link.click();
    URL.revokeObjectURL(url);
  }

  function exportSelectedCrewPdf() {
    const projectName = getProjectName(selectionProjectId) ?? "Not set";
    const printWindow = window.open("", "_blank", "noopener,noreferrer,width=960,height=720");
    if (!printWindow) {
      setError("Could not open print window");
      return;
    }

    const rows = selectedCrewItems.map((item) => `
      <tr>
        <td>${escapeHtml(item.name)}</td>
        <td>${escapeHtml(item.roles.join(", "))}</td>
        <td>${escapeHtml(item.email)}</td>
        <td>${escapeHtml(item.phone ?? "No phone")}</td>
        <td>${escapeHtml(projectName !== "Not set" ? projectName : item.project_name ?? "Not set")}</td>
      </tr>
    `).join("");

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Crewnovator Roster</title>
          <style>
            body { font-family: "IBM Plex Sans", "Segoe UI", sans-serif; margin: 32px; color: #102033; }
            h1 { margin: 0 0 8px; }
            p { margin: 0 0 20px; color: #56687b; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #d4dee7; padding: 10px 12px; text-align: left; vertical-align: top; }
            th { background: #eef4f8; }
          </style>
        </head>
        <body>
          <h1>Crew Roster / Contact Sheet</h1>
          <p>Project: ${escapeHtml(projectName)} | Generated from Crewnovator</p>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Roles</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Project</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    window.setTimeout(() => printWindow.print(), 250);
  }

  async function loadBootstrap(activeUser?: UserSessionView | null) {
    const currentUser = activeUser ?? session;
    if (!currentUser) {
      return;
    }

    const baseCalls = [
      apiRequest<{ roles: RoleRecord[] }>("/api/roles"),
      apiRequest<{ items: UnavailabilityRecord[] }>("/api/my-unavailability"),
      currentUser.app_role === "admin" || currentUser.app_role === "pm"
        ? apiRequest<{ projects: ProjectRecord[] }>("/api/projects")
        : Promise.resolve({ projects: [] }),
      currentUser.app_role === "admin"
        ? apiRequest<{ users: Array<UserListRecord & { role_ids: number[]; role_names: string[] }> }>("/api/admin/users")
        : Promise.resolve({ users: [] }),
    ] as const;

    const [rolesResponse, myBlocksResponse, projectsResponse, usersResponse] = await Promise.all(baseCalls);
    setRoles(rolesResponse.roles);
    setMyUnavailability(myBlocksResponse.items);
    setProjects(projectsResponse.projects);
    setUsers(usersResponse.users);

    if (currentUser.app_role === "admin" || currentUser.app_role === "pm") {
      await refreshTeamCalendar();
    }
  }

  async function refreshTeamCalendar() {
    const calendarResponse = await apiRequest<{ calendar: TeamCalendarResponse }>("/api/admin/unavailability");
    setTeamCalendar(calendarResponse.calendar);
  }

  async function refreshAdminUsers() {
    const usersResponse = await apiRequest<{ users: Array<UserListRecord & { role_ids: number[]; role_names: string[] }> }>("/api/admin/users");
    setUsers(usersResponse.users);
  }

  useEffect(() => {
    const handlePopState = () => {
      setAuthPath(window.location.pathname === "/login-code" ? "login-code" : "password");
    };
    window.addEventListener("popstate", handlePopState);
    void (async () => {
      try {
        const response = await apiRequest<{ user: UserSessionView }>("/api/me");
        setSession(response.user);
        setPage(navForRole(response.user.app_role)[0].key);
        await loadBootstrap(response.user);
      } catch (caught) {
        if (!(caught instanceof ApiError) || caught.status !== 401) {
          setError(caught instanceof Error ? caught.message : "Failed to load session");
        }
      } finally {
        setLoading(false);
      }
    })();
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  async function completeAuthenticatedLogin(user: UserSessionView) {
    setSession(user);
    setAuthPath("password");
    window.history.replaceState({}, "", "/");
    startTransition(() => {
      setPage(navForRole(user.app_role)[0].key);
    });
    await loadBootstrap(user);
  }

  async function handleLogin(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      const response = await apiRequest<{ user: UserSessionView }>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify(loginForm),
      });
      await completeAuthenticatedLogin(response.user);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Login failed");
    }
  }

  async function handleLogout() {
    await apiRequest("/api/auth/logout", { method: "POST" });
    setSession(null);
    setUsers([]);
    setProjects([]);
    setRoles([]);
    setMyUnavailability([]);
    setSearchResults([]);
    setTeamCalendar(null);
    setSelectedCrew({});
    setSelectionProjectId("");
  }

  async function refreshMyUnavailability() {
    const response = await apiRequest<{ items: UnavailabilityRecord[] }>("/api/my-unavailability");
    setMyUnavailability(response.items);
  }

  async function submitUnavailability(event: React.FormEvent) {
    event.preventDefault();
    try {
      await apiRequest("/api/my-unavailability", {
        method: "POST",
        body: JSON.stringify({
          start_date: unavailabilityForm.start_date,
          end_date: unavailabilityForm.end_date,
          reason: unavailabilityForm.reason,
          status: "active",
        }),
      });
      await refreshMyUnavailability();
      setUnavailabilityForm((current) => ({ ...current, reason: "" }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save unavailability");
    }
  }

  async function runCrewSearch(event: React.FormEvent) {
    event.preventDefault();
    await searchCrew();
  }

  async function searchCrew() {
    setError(null);
    try {
      const response = await apiRequest<{ results: CrewSearchResult[] }>(
        `/api/admin/find-crew?role_id=${searchForm.role_id}&start_date=${encodeURIComponent(searchForm.start_date)}&end_date=${encodeURIComponent(searchForm.end_date)}`,
      );
      setSearchResults(response.results);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Crew search failed");
    }
  }

  async function createProject(event: React.FormEvent) {
    event.preventDefault();
    try {
      await apiRequest("/api/projects", {
        method: "POST",
        body: JSON.stringify(projectForm),
      });
      const response = await apiRequest<{ projects: ProjectRecord[] }>("/api/projects");
      setProjects(response.projects);
      setProjectForm({ name: "", notes: "" });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not create project");
    }
  }

  async function createCommitment(result: CrewSearchResult, status: "hold" | "confirmed", override_conflicts = false) {
    try {
      await apiRequest("/api/commitments", {
        method: "POST",
        body: JSON.stringify({
          user_id: result.user_id,
          project_id: Number(searchForm.project_id),
          role_id: result.matched_role_id,
          start_date: searchForm.start_date,
          end_date: searchForm.end_date,
          status,
          notes: searchForm.notes,
          override_conflicts,
        }),
      });
      await searchCrew();
      if (session?.app_role === "admin" || session?.app_role === "pm") {
        await refreshTeamCalendar();
      }
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        const conflicts = Array.isArray((caught.details as { conflicts?: unknown[] })?.conflicts)
          ? ((caught.details as { conflicts: Array<{ id?: number; project_name?: string; created_by_name?: string; start_date: string; end_date: string }> }).conflicts)
          : [];
        const approved = window.confirm(
          `This crew member already has overlapping commitments.\n\n${conflicts.map((conflict) =>
            `${conflict.project_name ?? "Project"}${conflict.created_by_name ? `, claimed by ${conflict.created_by_name}` : ""}: ${formatDateRange(conflict.start_date, conflict.end_date)}`
          ).join("\n")}\n\nCreate this ${status} anyway?`,
        );
        if (approved) {
          await createCommitment(result, status, true);
        }
        return;
      }
      setError(caught instanceof Error ? caught.message : "Could not create commitment");
    }
  }

  async function releaseCommitment(commitment_id: number) {
    try {
      await apiRequest(`/api/commitments/${commitment_id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "released" }),
      });
      await searchCrew();
      if (session?.app_role === "admin" || session?.app_role === "pm") {
        await refreshTeamCalendar();
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not release commitment");
    }
  }

  async function openCrewProfile(user_id: number) {
    try {
      const [profileResponse, calendarResponse] = await Promise.all([
        apiRequest<{ user: UserListRecord & { role_names: string[]; email: string } }>(`/api/users/${user_id}`),
        apiRequest<{ calendar: TeamCalendarResponse }>(`/api/users/${user_id}/calendar`),
      ]);
      setSelectedCrewProfile(profileResponse.user);
      setSelectedCrewCalendar(calendarResponse.calendar);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load crew profile");
    }
  }

  async function saveUser(event: React.FormEvent) {
    event.preventDefault();
    try {
      if (userForm.id) {
        await apiRequest(`/api/admin/users/${userForm.id}`, {
          method: "PATCH",
          body: JSON.stringify({
            name: userForm.name,
            app_role: userForm.app_role,
            password: userForm.password,
            phone: userForm.phone,
            department: userForm.department,
            notes: userForm.notes,
            active: userForm.active,
          }),
        });
        await apiRequest(`/api/admin/users/${userForm.id}/roles`, {
          method: "PUT",
          body: JSON.stringify({
            role_ids: userForm.role_ids,
          }),
        });
      } else {
        await apiRequest("/api/admin/users", {
          method: "POST",
          body: JSON.stringify(userForm),
        });
      }

      await refreshAdminUsers();
      resetUserForm();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save user");
    }
  }

  function beginEditUser(user: UserListRecord & { role_ids?: number[] }) {
    setUserForm({
      id: user.id,
      email: user.email,
      password: "",
      name: user.name,
      app_role: user.app_role,
      phone: user.phone ?? "",
      department: user.department ?? "",
      notes: user.notes ?? "",
      active: user.active === 1,
      role_ids: user.role_ids ?? [],
    });
  }

  if (loading) {
    return <main className="app-shell centered"><p>Loading…</p></main>;
  }

  if (!session) {
    if (authPath === "login-code") {
      return (
        <LoginCodePage
          onLogin={completeAuthenticatedLogin}
          onBackToPassword={() => {
            window.history.replaceState({}, "", "/");
            setAuthPath("password");
          }}
        />
      );
    }

    return (
      <main className="login-layout">
        <section className="panel login-panel">
          <h1>Crewnovator</h1>
          <p className="muted">Internal crew staffing board for live event production.</p>
          <form className="stack" onSubmit={handleLogin}>
            <label>
              Email
              <input value={loginForm.email} onChange={(event) => setLoginForm((current) => ({ ...current, email: event.target.value }))} />
            </label>
            <label>
              Password
              <input type="password" value={loginForm.password} onChange={(event) => setLoginForm((current) => ({ ...current, password: event.target.value }))} />
            </label>
            <button type="submit">Log In</button>
            <button
              type="button"
              className="ghost"
              onClick={() => {
                window.history.pushState({}, "", "/login-code");
                setAuthPath("login-code");
              }}
            >
              Use Login Code Instead
            </button>
          </form>
          <p className="muted">Seed password for all demo users: <code>CrewOps123!</code></p>
          {error ? <p className="error-banner">{error}</p> : null}
        </section>
      </main>
    );
  }

  if (session.app_role === "crew" && session.role_confirmation_required === 1) {
    return <RoleConfirmationPage user={session} roles={roles} onConfirmed={setSession} />;
  }

  const navigation = navForRole(session.app_role);

  return (
    <main className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <h1>Crewnovator</h1>
          <p>{session.name}</p>
          <span className="tag neutral">{session.app_role}</span>
        </div>
        <nav className="nav-list">
          {navigation.map((item) => (
            <button
              key={item.key}
              className={page === item.key ? "nav-link active" : "nav-link"}
              onClick={() => startTransition(() => setPage(item.key))}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <button className="ghost" onClick={handleLogout}>Log Out</button>
      </aside>

      <section className="content">
        {error ? <p className="error-banner">{error}</p> : null}
        {session.app_role !== "crew" ? (
          <SelectedCrewTray
            items={selectedCrewItems}
            projects={projects}
            selectedProjectId={selectionProjectId}
            selectedProjectName={getProjectName(selectionProjectId)}
            onProjectChange={setSelectionProjectId}
            onRemove={(user_id) => {
              setSelectedCrew((current) => {
                const next = { ...current };
                delete next[user_id];
                return next;
              });
            }}
            onClear={clearSelectedCrew}
            onExportCsv={exportSelectedCrewCsv}
            onExportPdf={exportSelectedCrewPdf}
          />
        ) : null}

        {page === "my-unavailability" ? (
          <div className="stack page">
            <div className="page-header">
              <h2>My Unavailability</h2>
              <p>Keep personal blocks separate from project claims so PMs always see real conflicts.</p>
            </div>
            <form className="panel form-grid" onSubmit={submitUnavailability}>
              <label>
                Start
                <input type="date" value={unavailabilityForm.start_date} onChange={(event) => setUnavailabilityForm((current) => ({ ...current, start_date: event.target.value }))} />
              </label>
              <label>
                End
                <input type="date" value={unavailabilityForm.end_date} onChange={(event) => setUnavailabilityForm((current) => ({ ...current, end_date: event.target.value }))} />
              </label>
              <label>
                Reason
                <input value={unavailabilityForm.reason} onChange={(event) => setUnavailabilityForm((current) => ({ ...current, reason: event.target.value }))} />
              </label>
              <button type="submit">Add Block</button>
            </form>
            <div className="panel">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Start</th>
                    <th>End</th>
                    <th>Type</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {myUnavailability.map((item) => (
                    <tr key={item.id}>
                      <td>{formatDate(item.start_date)}</td>
                      <td>{formatDate(item.end_date)}</td>
                      <td>All day</td>
                      <td>{item.reason ?? "No reason"}</td>
                    </tr>
                  ))}
                  {myUnavailability.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="muted">No blocks entered.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {page === "find-crew" ? (
          <div className="stack page">
            <div className="page-header">
              <h2>Find Crew</h2>
              <p>Search by role and date range, then hold or confirm from the same screen.</p>
            </div>
            <form className="panel form-grid" onSubmit={runCrewSearch}>
              <label>
                Role
                <select value={searchForm.role_id} onChange={(event) => setSearchForm((current) => ({ ...current, role_id: event.target.value }))}>
                  <option value="">Select a role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </label>
              <label>
                Start
                <input type="date" value={searchForm.start_date} onChange={(event) => setSearchForm((current) => ({ ...current, start_date: event.target.value }))} />
              </label>
              <label>
                End
                <input type="date" value={searchForm.end_date} onChange={(event) => setSearchForm((current) => ({ ...current, end_date: event.target.value }))} />
              </label>
              <label>
                Project
                <select value={searchForm.project_id} onChange={(event) => setSearchForm((current) => ({ ...current, project_id: event.target.value }))}>
                  <option value="">Select project</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              </label>
              <label>
                Commitment notes
                <input value={searchForm.notes} onChange={(event) => setSearchForm((current) => ({ ...current, notes: event.target.value }))} />
              </label>
              <button type="submit">Search</button>
            </form>

            <div className="panel">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Crew</th>
                    <th>Status</th>
                    <th>Contacts</th>
                    <th>Conflicts</th>
                    <th>Earliest Open</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((result) => (
                    <tr key={result.user_id}>
                      <td>
                        <strong>{result.name}</strong>
                        <div className="muted">{result.matched_role_name}</div>
                        <div className="tag-row">
                          {result.all_role_names.map((role_name) => (
                            <span key={role_name} className="tag neutral">{role_name}</span>
                          ))}
                        </div>
                      </td>
                      <td><span className={`tag ${result.status === "Available" ? "available" : result.status === "Partially Available" ? "partial" : "blocked"}`}>{result.status}</span></td>
                      <td>
                        <a href={`tel:${result.phone ?? ""}`}>{result.phone ?? "No phone"}</a>
                        <a href={`mailto:${result.email}`}>{result.email}</a>
                      </td>
                      <td>
                        {result.conflicts.length === 0 ? (
                          <span className="muted">Open</span>
                        ) : (
                          <ul className="flat-list">
                            {result.conflicts.map((conflict, index) => (
                              <li key={`${result.user_id}-${index}`}>
                                {conflict.reason}
                                <br />
                                <span className="muted">{formatDateRange(conflict.start_date, conflict.end_date)}</span>
                                {conflict.kind === "commitment" && conflict.commitment_id ? (
                                  <button type="button" className="ghost compact-button" onClick={() => releaseCommitment(conflict.commitment_id ?? 0)}>
                                    Release
                                  </button>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td>{formatDate(result.earliest_available_date)}</td>
                      <td>
                        <div className="action-column">
                          <label className="checkbox-field compact">
                            <input
                              type="checkbox"
                              checked={selectedCrewIds.includes(result.user_id)}
                              onChange={() => toggleSelectedCrewFromSearchResult(result)}
                            />
                            <span>Select</span>
                          </label>
                          <button className="ghost" type="button" onClick={() => openCrewProfile(result.user_id)}>View Profile</button>
                          <button type="button" disabled={!searchForm.project_id} onClick={() => createCommitment(result, "hold")}>Hold</button>
                          <button type="button" disabled={!searchForm.project_id} onClick={() => createCommitment(result, "confirmed")}>Confirm</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {searchResults.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="muted">Run a search to see crew status.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {page === "team-calendar" ? (
          <div className="stack page">
            <div className="page-header">
              <h2>Team Calendar</h2>
              <p>Red blocks are personal. Slate blocks are project commitments.</p>
            </div>
            <div className="panel">
              {teamCalendar ? (
                <CalendarView
                  mode="team"
                  users={teamCalendar.users}
                  events={teamCalendar.events}
                  allowedViews={["week", "month", "year"]}
                  initialView="week"
                  initialDate={defaultRange.start}
                  selectedUserIds={selectedCrewIds}
                  onToggleUserSelection={toggleSelectedCrewFromCalendar}
                  onOpenUserProfile={openCrewProfile}
                />
              ) : (
                <p className="muted">No calendar data loaded.</p>
              )}
            </div>
          </div>
        ) : null}

        {page === "projects" ? (
          <div className="stack page">
            <div className="page-header">
              <h2>Projects</h2>
              <p>Create projects first, then claim crew from Find Crew.</p>
            </div>
            <form className="panel form-grid" onSubmit={createProject}>
              <label>
                Name
                <input value={projectForm.name} onChange={(event) => setProjectForm((current) => ({ ...current, name: event.target.value }))} />
              </label>
              <label>
                Notes
                <input value={projectForm.notes} onChange={(event) => setProjectForm((current) => ({ ...current, notes: event.target.value }))} />
              </label>
              <button type="submit">Create Project</button>
            </form>
            <div className="panel">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>PM</th>
                    <th>Notes</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project) => (
                    <tr key={project.id}>
                      <td>{project.name}</td>
                      <td>{project.pm_name ?? project.pm_user_id}</td>
                      <td>{project.notes ?? "—"}</td>
                      <td>{new Date(project.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {page === "crew" ? (
          <div className="stack page">
            <div className="page-header">
              <h2>Crew</h2>
              <p>Create users, update crew profiles, and assign production roles.</p>
            </div>
            <form className="panel form-grid" onSubmit={saveUser}>
              <label>
                Name
                <input value={userForm.name} onChange={(event) => setUserForm((current) => ({ ...current, name: event.target.value }))} />
              </label>
              <label>
                Email
                <input value={userForm.email} disabled={Boolean(userForm.id)} onChange={(event) => setUserForm((current) => ({ ...current, email: event.target.value }))} />
              </label>
              <label>
                Password
                <input type="password" value={userForm.password} onChange={(event) => setUserForm((current) => ({ ...current, password: event.target.value }))} />
                {userForm.id ? <span className="muted">Leave blank to keep the current password.</span> : null}
              </label>
              <label>
                App Role
                <select value={userForm.app_role} onChange={(event) => setUserForm((current) => ({ ...current, app_role: event.target.value as UserSessionView["app_role"] }))}>
                  <option value="crew">crew</option>
                  <option value="pm">pm</option>
                  <option value="admin">admin</option>
                </select>
              </label>
              <label>
                Phone
                <input value={userForm.phone} onChange={(event) => setUserForm((current) => ({ ...current, phone: event.target.value }))} />
              </label>
              <label>
                Department
                <input value={userForm.department} onChange={(event) => setUserForm((current) => ({ ...current, department: event.target.value }))} />
              </label>
              <label className="full-span">
                Notes
                <textarea value={userForm.notes} onChange={(event) => setUserForm((current) => ({ ...current, notes: event.target.value }))} />
              </label>
              <label className="checkbox-field">
                <input type="checkbox" checked={userForm.active} onChange={(event) => setUserForm((current) => ({ ...current, active: event.target.checked }))} />
                Active
              </label>
              <div className="full-span role-checkboxes">
                {roles.map((role) => (
                  <label key={role.id} className="checkbox-chip">
                    <input
                      type="checkbox"
                      checked={userForm.role_ids.includes(role.id)}
                      onChange={(event) =>
                        setUserForm((current) => ({
                          ...current,
                          role_ids: event.target.checked
                            ? [...current.role_ids, role.id]
                            : current.role_ids.filter((value) => value !== role.id),
                        }))
                      }
                    />
                    {role.name}
                  </label>
                ))}
              </div>
              <button type="submit">{userForm.id ? "Update User" : "Create User"}</button>
              {userForm.id ? <button type="button" className="ghost" onClick={resetUserForm}>Cancel Edit</button> : null}
            </form>
            <div className="panel">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>App Role</th>
                    <th>Contacts</th>
                    <th>Production Roles</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.name}</td>
                      <td>{user.app_role}</td>
                      <td>
                        <a href={`tel:${user.phone ?? ""}`}>{user.phone ?? "No phone"}</a>
                        <a href={`mailto:${user.email}`}>{user.email}</a>
                      </td>
                      <td>
                        <div className="tag-row">
                          {(user.role_names ?? []).map((role_name) => (
                            <span key={role_name} className="tag neutral">{role_name}</span>
                          ))}
                        </div>
                      </td>
                      <td><button type="button" className="ghost" onClick={() => beginEditUser(user)}>Edit</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {page === "add-crew-accounts" ? (
          <AddCrewAccountsPage
            roles={roles}
            existingEmails={users.map((user) => user.email)}
            onImported={async () => {
              await refreshAdminUsers();
            }}
          />
        ) : null}

        {page === "onboard-crew-member" ? (
          <OnboardCrewMemberPage
            roles={roles}
            onSent={async () => {
              await refreshAdminUsers();
            }}
          />
        ) : null}

        {page === "roles" ? (
          <div className="stack page">
            <div className="page-header">
              <h2>Roles</h2>
              <p>Seed production roles used by Find Crew and crew commitments.</p>
            </div>
            <div className="panel role-list">
              {roles.map((role) => (
                <div key={role.id} className="role-row">
                  <strong>{role.name}</strong>
                  <span>{users.filter((user) => user.role_ids?.includes(role.id)).length} assigned</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </section>

      <CrewProfileDrawer
        open={Boolean(selectedCrewProfile)}
        profile={selectedCrewProfile}
        calendar={selectedCrewCalendar}
        onClose={() => {
          setSelectedCrewProfile(null);
          setSelectedCrewCalendar(null);
        }}
      />
    </main>
  );
}
